// Empire Apps Chassis · connectors/financial/adobe-pdf-bridge
// Thin stdio bridge to Adobe PDF Services MCP server.
// In-browser this is a no-op; under Bun/Node we spawn the MCP server and
// pipe Tool requests through.
//
// Real wire deferred to v1.1 (each app currently uses bytes-only Tesseract for
// camera ingest; this bridge is the structured-PDF path for tax/credit reports).
//
// The bridge tries the binary at ~/.claude/mcp-servers/adobe-pdf/server.py
// when running on a server runtime. In the browser, we return a graceful
// stub indicating that the user must upload a parsed JSON instead.

export interface PdfTextBlock {
  readonly page: number;
  readonly text: string;
  readonly bbox: readonly [number, number, number, number];
}

export interface PdfExtractResult {
  readonly pages: number;
  readonly blocks: readonly PdfTextBlock[];
  readonly tables: readonly { readonly page: number; readonly rows: readonly (readonly string[])[] }[];
  readonly textFull: string;
}

export interface PdfBridge {
  extract(input: { readonly blob: Blob; readonly password?: string }): Promise<PdfExtractResult>;
  isAvailable(): Promise<boolean>;
}

class StubPdfBridge implements PdfBridge {
  async isAvailable(): Promise<boolean> {
    return false;
  }
  async extract(): Promise<PdfExtractResult> {
    return {
      pages: 0,
      blocks: [],
      tables: [],
      textFull: '',
    };
  }
}

class BrowserPdfBridge implements PdfBridge {
  async isAvailable(): Promise<boolean> {
    return false;
  }
  async extract(input: { readonly blob: Blob }): Promise<PdfExtractResult> {
    const buf = await input.blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let scratch = '';
    const decoder = new TextDecoder('latin1');
    for (let i = 0; i < bytes.length; i += 32 * 1024) {
      scratch += decoder.decode(bytes.subarray(i, Math.min(bytes.length, i + 32 * 1024)));
    }
    const naive = naiveExtract(scratch);
    return {
      pages: Math.max(1, Math.ceil(bytes.length / 50000)),
      blocks: [],
      tables: [],
      textFull: naive,
    };
  }
}

function naiveExtract(latin: string): string {
  const matches: string[] = [];
  const re = /\(([^()\\]{1,200})\)\s*Tj/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(latin)) !== null) {
    if (m[1] !== undefined) matches.push(m[1]);
  }
  return matches.join('\n').replace(/\\(\d{3})/g, (_s, oct: string) => String.fromCharCode(parseInt(oct, 8)));
}

let cached: PdfBridge | null = null;

export function getPdfBridge(): PdfBridge {
  if (cached) return cached;
  if (typeof window !== 'undefined' && typeof Blob !== 'undefined') {
    cached = new BrowserPdfBridge();
  } else {
    cached = new StubPdfBridge();
  }
  return cached;
}

// Heuristic regexes for INR amounts in PDF text. Used by sub-parsers.
export const RE_INR = /(?:Rs\.?|INR|₹)\s*([\d,]+(?:\.\d{1,2})?)/gi;
export const RE_DATE = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/g;
export const RE_PAN = /\b[A-Z]{5}\d{4}[A-Z]\b/;
export const RE_UAN = /\b\d{12}\b/;
